/* sts-logout.js — sign-out and shared-device hygiene (work item G19).
 *
 * Spec: docs/new-theme-v4/specs/02-session-states-accounts-routing-spec.md
 *       §3.6 (the logout list), §7.8 (both mechanisms), decision 02-Q13
 *       ("clear everything identity-shaped on logout").
 *
 * When a customer signs out, the next person on that device must not inherit
 * their skin type, routine, quiz progress or anything else that personalises
 * the site. assets/sts-state.js already clears the spec's §3.6 list. This file
 * is the complete sweep: it covers every key the theme — v4 and the legacy code
 * still running beside it — stores about a person, which is longer than the
 * spec's list, and it keeps working whichever of the two scripts loads first.
 *
 * Three triggers, all idempotent:
 *
 *   1. The click. Any link to /account/logout (or a localised /xx-yy/account/
 *      logout), capture phase so no other handler can swallow it first. The
 *      anchor's own href does the navigation.
 *
 *   2. The server said so. snippets/sts-server-facts.liquid runs in <head>,
 *      before anything can rewrite the evidence, and sets STS.logoutReason:
 *        'signout' — the page rendered signed out, but this device was last
 *                    used signed in (session expiry, a logout in another tab,
 *                    a logout from Shopify's own pages where no hook ran)
 *        'switch'  — a different customer is signed in now (a Multipass login
 *                    straight over an old session never shows a signed-out page)
 *      If that snippet is not rendered, the resolver's own STS.clearIdentity
 *      flag and sts_last_customer_id are used instead, as far as they reach.
 *
 *   3. Back button after sign-out. A signed-in page restored from the
 *      back/forward cache still shows the previous customer's page from memory.
 *      If this device no longer belongs to the customer that page was rendered
 *      for, it is reloaded, which renders it signed out.
 *
 * Exposes STS.clearOnLogout() for flows that sign someone out or switch
 * accounts themselves (the auth modal, W09).
 */
(function () {
  'use strict';

  var w = window;
  var d = document;
  var STS = (w.STS = w.STS || {});
  if (STS.__logoutHygiene) return;          // loaded twice: the first copy wins
  STS.__logoutHygiene = true;

  var SERVER = w.__STS_SERVER__ || {};

  /* ------------------------------------------------------------ the lists
     Every key is here for a reason, and so is every key that is NOT here.

     KEPT, deliberately:
       affiliateCode, fullCodeParam, providerId, affiliateProducts,
       stsAffiliateLogoCache      affiliate attribution survives a sign-out
                                  (decision 03-Q4); never add them
       checkout_url, sts_return_to, sts_auth_intent
                                  post-auth return targets. The staged and claim
                                  flows (sts-liquid-quiz.js:2091,
                                  sts-liquid-quiz-results.js:3582) sign the
                                  customer out ON PURPOSE and need these to
                                  survive it to land on the claim prompt
       sts_preview                staff simulator, tab-scoped, never real data
       stsAnnPaused, sts_grid_density, sts_steps_print_help_seen,
       sts.library.index.v2, announcementBarDismissed, klaviyoPopupDismissed,
       quizContentClosed, stopAutoFilteringBySkinType, filtersManuallyCleared
                                  device and display preferences, or a content
                                  cache, that say nothing about a person
       slq_session_uuid, slq_client_seq, stsSession
                                  analytics session ids, not personalisation
       needToUpdateDoctorRoutineAfterLogIn
                                  snippets/quiz.liquid re-sets it on every
                                  signed-out page; clearing it is a no-op        */

  /* localStorage, cleared on every sign-out. */
  var LOCAL = [
    // The state layer's own list (assets/sts-state.js IDENTITY_LOCAL, spec §3.6),
    // repeated so this file is complete whichever script runs first.
    'customerSkinType', 'customerSensitivityList', 'customerEmail', 'customerOid',
    'customerRoutine', 'customerQuizAnswers', 'purchasedSkus', 'isLoggedIn',
    'sts_typed_at', 'sts_quiz_progress', 'sts_last_customer_id', 'sts_fresh_type',
    'slqSavedResults', 'slqSavedRegimen',
    // Account mirrors and hand-off flags from snippets/quiz.liquid and the
    // retired quiz-results.js. The needTo* flags are the dangerous ones: they
    // tell the next sign-in to write THIS device's quiz data into whichever
    // account signs in next.
    'isDoctorRoutineUsing', 'needToUpdateCustomerMetafields', 'needToUpdateCustomerRoutine',
    'needToSendThirdPartyRequests', 'isNewCustomer',
    // A ZIP code typed into the newsletter popup (assets/klaviyo-popup.js).
    'customerZip',
    // Library topic picks. assets/sts-library.js keeps them here for signed-in
    // readers too while its sync seam is off, and merges them into the next
    // account that signs in.
    'sts.profile.guest',
    // Legacy device-wide check-in snooze. Nothing reads it now, so it is inert
    // rather than wrong; named here so it does not sit in storage forever. NOT
    // migrated: adopting a device-wide value into a per-customer bucket hands
    // whoever is signed in somebody else's snooze, which is the bug. Cost: a
    // dismissal made under the old key is seen once more.
    'sts_checkin_snooze'
  ];
  /* Skin Profile choices about this person's routine: the Subscribe & Save
     opt-out and the AM/PM tab (assets/sts-profile.js). */
  /* sts.sp. is the skin-profile caches. sts_checkin_snooze_ is the check-in
     strip's × (02-Q18), which was on the KEPT list above until it was keyed per
     customer: one shared key let the first person to dismiss the strip silence
     it for everyone else on the browser, and a key naming a customer must not
     outlive that session on a shared device (§7.8). Both halves ship together —
     the per-customer key is not acceptable without this line. Cost: sign out
     and back in, or switch accounts, and the strip returns once. */
  var LOCAL_PREFIXES = ['sts.sp.', 'sts_checkin_snooze_'];

  /* sessionStorage (this tab), cleared on every sign-out. */
  var SESSION = [
    'slqResultsHandoff', 'slq_result',       // the result blob, twice (spec §3.6 + sts-state.js)
    'slqQuizProgress',                        // quiz progress; the bridge would re-persist it
    'customerSkinType', 'customerSensitivityList', // legacy session copies header code used to read
    'firstname', 'lastname',                  // name typed into snippets/customer-quiz-scripts.liquid
    'sts.library.merged'                      // so the NEXT account's sign-in merges its own topics
  ];
  /* The approved-only collection toggle and its per-URL loop guards
     (assets/sts-collection.js): a type-driven filter left on for the next person. */
  var SESSION_PREFIXES = ['sts_fits_'];

  /* On an account switch the page has just written the NEW customer's account
     mirrors (snippets/quiz.liquid, then sts-state.js reconcile). Removing those
     would only blank the new customer's copies until the next page, so a switch
     sweeps everything except them. sts-state.js clears and rewrites them itself. */
  var REWRITTEN_ON_SIGNIN = [
    'customerSkinType', 'customerSensitivityList', 'customerEmail', 'customerOid',
    'customerRoutine', 'customerQuizAnswers', 'purchasedSkus', 'isLoggedIn',
    'isDoctorRoutineUsing', 'sts_typed_at', 'sts_last_customer_id'
  ];

  /* -------------------------------------------------------------- storage */

  function store(name) {
    try { return w[name]; } catch (e) { return null; }  // access itself throws when blocked
  }
  function get(key) {
    var s = store('localStorage');
    try { return s ? s.getItem(key) : null; } catch (e) { return null; }
  }
  function usable() {
    var s = store('localStorage');
    try { s.setItem('__sts_lo__', '1'); s.removeItem('__sts_lo__'); return true; } catch (e) { return false; }
  }

  function sweep(name, keys, prefixes, except) {
    var s = store(name);
    if (!s) return;
    var i;
    var doomed = [];
    for (i = 0; i < keys.length; i++) {
      if (!except || except.indexOf(keys[i]) < 0) doomed.push(keys[i]);
    }
    if (prefixes && prefixes.length) {
      try {
        for (i = 0; i < s.length; i++) {
          var k = s.key(i);
          for (var p = 0; p < prefixes.length; p++) {
            if (k && k.indexOf(prefixes[p]) === 0) { doomed.push(k); break; }
          }
        }
      } catch (e) { /* blocked storage */ }
    }
    // Collected first, removed second: removing while indexing s.key(i) skips keys.
    for (i = 0; i < doomed.length; i++) {
      try { s.removeItem(doomed[i]); } catch (e) { /* blocked storage */ }
    }
  }

  function clearAll() {
    sweep('localStorage', LOCAL, LOCAL_PREFIXES);
    sweep('sessionStorage', SESSION, SESSION_PREFIXES);
  }

  function clearForSwitch() {
    sweep('localStorage', LOCAL, LOCAL_PREFIXES, REWRITTEN_ON_SIGNIN);
    sweep('sessionStorage', SESSION, SESSION_PREFIXES, REWRITTEN_ON_SIGNIN);
  }

  STS.clearOnLogout = clearAll;

  /* ------------------------------------------------- 1. the sign-out click */

  function isLogoutHref(href) {
    if (!href) return false;
    var path = href.split('#')[0].split('?')[0];
    return /\/account\/logout\/?$/.test(path) || /\/customer_authentication\/logout\/?$/.test(path);
  }

  d.addEventListener('click', function (ev) {
    var el = ev.target;
    while (el && el !== d) {
      if (el.tagName === 'A') {
        if (isLogoutHref(el.getAttribute('href'))) clearAll();
        return;
      }
      el = el.parentNode;
    }
  }, true);

  /* ------------------------------------------- 2. the server's sign-out */

  var reason = STS.logoutReason;
  if (!reason && !SERVER.designMode) {
    // snippets/sts-server-facts.liquid not rendered: fall back to what the
    // resolver itself noticed, if sts-state.js has not consumed it yet.
    if (STS.clearIdentity) reason = SERVER.loggedIn ? 'switch' : 'signout';
    else if (!SERVER.loggedIn && get('sts_last_customer_id')) reason = 'signout';
  }
  if (reason === 'signout') clearAll();
  else if (reason === 'switch') clearForSwitch();

  /* ------------------------------------------ 3. back/forward cache restore */

  w.addEventListener('pageshow', function (ev) {
    if (!ev.persisted || !SERVER.loggedIn || !SERVER.customerId) return;
    if (!usable()) return;   // blocked storage cannot prove anything; never loop
    if (get('sts_last_customer_id') !== String(SERVER.customerId)) w.location.reload();
  });
}());
